import { Participants } from "@/types/bookinghome";
import { Minus, Plus } from "lucide-react";

interface ParticipantSelectorProps {
  participants: Participants;
  onChange: (participants: Participants) => void;
  maxParticipants: number;
  pricing: {
    adults: { amount: number; currency: string };
    children: { amount: number; currency: string };
    students: { amount: number; currency: string };
  };
  isLoading?: boolean; 
}

export const ParticipantSelector = ({
  participants,
  onChange,
  maxParticipants,
  pricing,
}: ParticipantSelectorProps) => {
  const totalParticipants =
    participants.adults + participants.children + participants.students;

  const updateParticipants = (
    type: keyof Participants,
    increment: boolean
  ) => {
    const newValue = increment
      ? participants[type] + 1
      : Math.max(0, participants[type] - 1);

    if (
      increment &&
      totalParticipants >= maxParticipants
    ) {
      return;
    }

    onChange({
      ...participants,
      [type]: newValue,
    });
  };

  return (
    <div className="space-y-4">
      <div className="participant-counter">
        <div className="flex-1">
          <div className="font-medium">Adults</div>
          <div className="text-sm text-muted-foreground">Age 18+</div>
            <div className="text-sm font-medium">{pricing.adults.amount} {pricing.adults.currency} per person</div>
        </div>
        <button
          className="counter-button"
          onClick={() => updateParticipants("adults", false)}
          disabled={participants.adults === 0}
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-8 text-center">{participants.adults}</span>
        <button
          className="counter-button"
          onClick={() => updateParticipants("adults", true)}
          disabled={totalParticipants >= maxParticipants}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="participant-counter">
        <div className="flex-1">
          <div className="font-medium">Children</div>
          <div className="text-sm text-muted-foreground">Age 4-17</div>
          <div className="text-sm font-medium">{pricing.children.amount} {pricing.children.currency} per person</div>
        </div>
        <button
          className="counter-button"
          onClick={() => updateParticipants("children", false)}
          disabled={participants.children === 0}
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-8 text-center">{participants.children}</span>
        <button
          className="counter-button"
          onClick={() => updateParticipants("children", true)}
          disabled={totalParticipants >= maxParticipants}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="participant-counter">
        <div className="flex-1">
          <div className="font-medium">Students</div>
          <div className="text-sm text-muted-foreground">
            Valid student ID required
          </div>
          <div className="text-sm font-medium">{pricing.students.amount} {pricing.students.currency} per person</div>
        </div>
        <button
          className="counter-button"
          onClick={() => updateParticipants("students", false)}
          disabled={participants.students === 0}
        >
          <Minus className="h-4 w-4" />
        </button>
        <span className="w-8 text-center">{participants.students}</span>
        <button
          className="counter-button"
          onClick={() => updateParticipants("students", true)}
          disabled={totalParticipants >= maxParticipants}
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <div className="text-sm text-muted-foreground mt-4">
        {maxParticipants - totalParticipants} spots remaining
      </div>
    </div>
  );
};
